/**
 * localDb — typed helpers for SQLite IPC calls in the Electron renderer.
 *
 * Three DB interfaces:
 *   localDb(craftspaceId)  — craftspace-scoped DB for a given craftspace
 *   appDb                 — app-level DB (craftspaces registry)
 *   allCraftspacesDb       — aggregate read across all craftspace DBs
 *
 * Only use this module inside code that runs in Electron (guard with isElectron()).
 */

export type DbRow = Record<string, unknown>;

function assertElectron(): void {
  if (!window.electronAPI) {
    throw new Error("localDb: window.electronAPI is not available. Are you running in Electron?");
  }
}

// ─── Craftspace-scoped DB ─────────────────────────────────────────────────────

export function localDb(craftspaceId: string) {
  return {
    async query<T = DbRow>(sql: string, params?: unknown[]): Promise<T[]> {
      assertElectron();
      const rows = await window.electronAPI!.db.query(sql, params, craftspaceId);
      return rows as T[];
    },

    async get<T = DbRow>(sql: string, params?: unknown[]): Promise<T | undefined> {
      assertElectron();
      const row = await window.electronAPI!.db.get(sql, params, craftspaceId);
      return row as T | undefined;
    },

    async run(
      sql: string,
      params?: unknown[],
    ): Promise<{ changes: number; lastInsertRowid: string }> {
      assertElectron();
      return window.electronAPI!.db.run(sql, params, craftspaceId);
    },

    async transaction(
      operations: Array<{ sql: string; params?: unknown[] }>,
    ): Promise<Array<{ changes: number; lastInsertRowid: string }>> {
      assertElectron();
      return window.electronAPI!.db.transaction(operations, craftspaceId);
    },
  };
}

// ─── App-level DB (craftspaces registry) ─────────────────────────────────────

export const appDb = {
  async query<T = DbRow>(sql: string, params?: unknown[]): Promise<T[]> {
    assertElectron();
    const rows = await window.electronAPI!.db.appQuery(sql, params);
    return rows as T[];
  },

  async get<T = DbRow>(sql: string, params?: unknown[]): Promise<T | undefined> {
    assertElectron();
    const row = await window.electronAPI!.db.appGet(sql, params);
    return row as T | undefined;
  },

  async run(
    sql: string,
    params?: unknown[],
  ): Promise<{ changes: number; lastInsertRowid: string }> {
    assertElectron();
    return window.electronAPI!.db.appRun(sql, params);
  },

  async transaction(
    operations: Array<{ sql: string; params?: unknown[] }>,
  ): Promise<Array<{ changes: number; lastInsertRowid: string }>> {
    assertElectron();
    return window.electronAPI!.db.appTransaction(operations);
  },
};

// ─── Aggregate all-craftspaces DB (read-only) ─────────────────────────────────

export const allCraftspacesDb = {
  async query<T = DbRow>(sql: string, params?: unknown[]): Promise<T[]> {
    assertElectron();
    const rows = await window.electronAPI!.db.allQuery(sql, params);
    return rows as T[];
  },
};

// ─── Utilities ───────────────────────────────────────────────────────────────

/** Generate a UUID for local records. */
export function newId(): string {
  return crypto.randomUUID();
}

/** Current ISO timestamp */
export function now(): string {
  return new Date().toISOString();
}
